import React, { useState } from "react";
import { motion } from "framer-motion";
import Heading from "../Typography/Heading";
import Paragraph from "../Typography/Paragraph";
import SpecialText from "../Typography/SpecialText";
import {
  PUBLIC_ABOUT_COLUMN_GAP_CLASS,
  PUBLIC_CARD_GRID_GAP_CLASS,
  PUBLIC_HEADING_TO_CONTENT_CLASS,
} from "../../../sections/Public/shared/publicLayoutSpec";

const ComparisonSection = () => {
  const [flippedCards, setFlippedCards] = useState({});

  const handleCardClick = (cardId) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const comparisonData = [
    {
      value: "20%",
      valueSub: "Commission",
      description: "They keep a big cut from every job.",
      backValue: "0%",
      backValueSub: "Commission",
      backDescription: "You keep what you earn — no platform cuts."
    },
    {
      value: "$50",
      valueSub: "to Bid",
      description: "You pay upfront just to quote.",
      backValue: "$0",
      backValueSub: "to Quote",
      backDescription: "No bidding. Customers contact you directly."
    },
    // {
    //   value: "5x",
    //   valueSub: "Lead Resale",
    //   description: "They sell the same lead to multiple tradies.",
    //   backValue: "1-to-1",
    //   backValueSub: "Leads",
    //   backDescription: "Real customers. No shared or recycled leads."
    // },
    {
      value: "$300",
      valueSub: "Boost Fees",
      description: "They charge more for visibility.",
      backValue: "No",
      backValueSub: "Boosting Fees",
      backDescription: "Post visibility is based on location, not payments."
    },
    {
      value: "100+",
      valueSub: "Credit Trap",
      description: "Top up, spend, repeat — or get locked out.",
      backValue: "Flat",
      backValueSub: "Monthly Fee",
      backDescription: "No credit packs. One price covers everything."
    },
    // {
    //   value: "12",
    //   valueSub: "Month Lock-In",
    //   description: "You're stuck in long contracts with penalties.",
    //   backValue: "No",
    //   backValueSub: "Lock-In",
    //   backDescription: "Cancel anytime. No hidden conditions."
    // },
    {
      value: "50%",
      valueSub: "Undercut Pressure",
      description: "Race to the bottom just to win jobs.",
      backValue: "You",
      backValueSub: "Set The Rate",
      backDescription: "No forced discounts. Stand by your pricing."
    },
    {
      value: "$5",
      valueSub: "per Reply",
      description: "They charge again when you respond to leads.",
      backValue: "Unlimited",
      backValueSub: "Messaging",
      backDescription: "Contact freely. No reply fees."
    },
    {
      value: "10%",
      valueSub: "Job Cut",
      description: "They take a cut from every invoice.",
      backValue: "Invoice",
      backValueSub: "Customers Directly",
      backDescription: "Set your own payment terms."
    },
    // {
    //   value: "$0",
    //   valueSub: "Core Tools Access",
    //   description: "Basic plans miss key tools unless you upgrade.",
    //   backValue: "Everything",
    //   backValueSub: "Included",
    //   backDescription: "No essential features locked behind paywalls."
    // },
    // {
    //   value: "1st",
    //   valueSub: "Rank = Highest Bidder",
    //   description: "Only those who pay most are seen.",
    //   backValue: "Ranked",
    //   backValueSub: "By Relevance",
    //   backDescription: "Customers see you based on trade and location — not how much you pay."
    // }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const slideFromLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 }
  };

  const staggerLeftContent = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.1
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const renderCard = (data, cardId, isFlipped) => {
    return (
      <motion.div 
        className="relative h-[320px] w-full cursor-pointer"
        variants={fadeInUp}
        transition={{ duration: 0.6, ease: "easeOut" }}
        onClick={() => handleCardClick(cardId)}
        style={{ perspective: "1000px" }}
      >
        <motion.div
          className="relative w-full h-full"
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{ 
            transformStyle: "preserve-3d",
            transformOrigin: "center center"
          }}
        >
          {/* Front Side */}
          <div 
            className="absolute inset-0 rounded-xl bg-lightgray p-8 flex flex-col justify-between items-start text-left"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden"
            }}
          >
            <div>
              <SpecialText variant="number-xl" className="!font-bold !leading-[1.2]">
                {data.value}
              </SpecialText>
              {data.valueSub && (
                <Paragraph variant="MainParagraph" className="block !font-bold mt-0">
                  {data.valueSub}
                </Paragraph>
              )}
            </div>
            <Paragraph variant="BodyCompact">
              {data.description}
            </Paragraph>
          </div>
          
          {/* Back Side */}
          <div 
            className="absolute inset-0 rounded-xl bg-primaryblue p-8 pt-12 flex flex-col justify-between items-start text-left"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)"
            }}
          >
                        <div>
              {(/[\d$%]/.test(data.backValue) && !/[a-zA-Z]/.test(data.backValue)) ? (
                <SpecialText 
                  variant="number-xl"
                  className="!font-bold text-white !leading-[1.2]"
                >
                  {data.backValue}
                </SpecialText>
              ) : (
                <Heading variant="h3" className="!font-bold text-white !leading-[1.2]">
                  {data.backValue}
                </Heading>
              )}
              {data.backValueSub && (
                <Paragraph variant="MainParagraph" className="block !font-bold text-white mt-0">
                  {data.backValueSub}
                </Paragraph>
              )}
            </div>
            <Paragraph variant="BodyCompact" className="text-white">
              {data.backDescription}
            </Paragraph>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="tp-container-main mx-auto">
      <div className={`grid grid-cols-1 lg:grid-cols-2 ${PUBLIC_ABOUT_COLUMN_GAP_CLASS}`}>
        {/* Left Content */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerLeftContent}
        >
          <motion.div 
            variants={slideFromLeft}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Heading variant={"h2"} className={"font-extrabold text-offblack"}>
              Trades Poster
              <span className="uppercase italic"><br />VS </span>
              the Rest
            </Heading>
          </motion.div>
          
          {/* <motion.div 
            variants={slideFromLeft}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <SpecialText variant={"frame-xs"} className={"mb-6 text-offblack italic font-medium pr-16 leading-normal"}>
              Why settle for less when you can have the best
            </SpecialText>
          </motion.div> */}
          
          {/* <Paragraph variant={"MainParagraph"} className={"text-offblack"}>
            Customers contact you directly through mobile, messaging, email, or social media. 
            You manage quotes, set payment terms, and keep every dollar you earn
          </Paragraph> */}
        </motion.div>

        {/* Right Content - Comparison Cards */}
        <motion.div
          className={`grid grid-cols-2 ${PUBLIC_CARD_GRID_GAP_CLASS} ${PUBLIC_HEADING_TO_CONTENT_CLASS} lg:mt-0`}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          {comparisonData.map((item, index) => (
            renderCard(item, `card-${index}`, flippedCards[`card-${index}`])
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ComparisonSection; 